import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../services/api";
import { Card, Spin, Typography } from "antd";
import { LoadingOutlined } from '@ant-design/icons';
import LoadingPage from "src/components/loadingPage/LoadingPage";
import { useDispatch } from "react-redux";
import { setLanguage as setLanguageStore } from "../store/themeSlice";

const LanguageContext = createContext({
  language: "EN",
  languages: [],
  translations: {},
  setLanguage: () => { },
  refreshLanguages: async () => { },
  refreshTranslations: async () => { },
  t: (defKey, fallback) => fallback ?? defKey,
});
const getDefaultLanguage = () => {
  const saved = localStorage.getItem("appLanguage");
  if (saved) return saved;

  // Tarayıcı dilini al
  const browserLang = navigator.language || navigator.userLanguage; // örn: "en-US"
  const shortLang = browserLang.split("-")[0].toUpperCase(); // örn: "EN"

  // Desteklenen dillerden birini döndür, yoksa TR
  return ["TR", "EN","NL","DE","FR"].includes(shortLang) ? shortLang : "EN";
};

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(getDefaultLanguage);
  const [languages, setLanguages] = useState([]);
  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(false);


  const [firstMount, setFirstMount] = useState(true);

  const dispatch = useDispatch();


  useEffect(() => {

    if (language)
      dispatch(setLanguageStore(language));
    else
      dispatch(setLanguageStore(null));

  }, [language]);



  const refreshLanguages = useCallback(async () => {
    try {
      const res = await api.get("/languages");
      const data = res?.data?.items || res?.data || [];
      setLanguages(Array.isArray(data) ? data : []);
      setFirstMount(false);
    } catch (e) {
      setLanguages([]);
    }
  }, []);

  const refreshTranslations = useCallback(async (langCode = language) => {
    setLoading(true);
    try {
      const lc = (langCode || language || "").toString().toUpperCase();
      const res = await api.get("/translations", { params: { languageCode: lc } });
      const raw = res?.data.items ?? res?.data ?? [];
      const list = Array.isArray(raw) ? raw : (raw ? [raw] : []);
      const map = {};
      list.forEach((item) => {
        if (!item) return;
        const key = item.def ?? item.Def ?? item.key;
        const val = item.value ?? item.Value ?? item.translation ?? "";
        if (key) map[key] = val;
      });
      setTranslations(map);
    } catch (e) {
      setTranslations({});
    } finally {
      setLoading(false);
    }
  }, [language]);

  useEffect(() => {
    refreshLanguages();
  }, [refreshLanguages]);

  useEffect(() => {
    localStorage.setItem("appLanguage", language);
    refreshTranslations(language);
  }, [language, refreshTranslations]);

  const t = useCallback((defKey, fallback) => {
    if (!defKey) return fallback ?? "";
    return translations[defKey] ?? fallback ?? defKey;
  }, [translations]);

  const value = useMemo(() => ({
    language,
    languages,
    translations,
    loading,
    setLanguage,
    refreshLanguages,
    refreshTranslations,
    t,
  }), [language, languages, translations, loading, t, refreshLanguages, refreshTranslations]);




  if (firstMount && loading) {
    return (<LoadingPage />);
  }


  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export default LanguageProvider;


